import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Gift, 
  Calendar, 
  Mail, 
  Settings, 
  BarChart3, 
  Users, 
  Send, 
  Loader2, 
  Check, 
  X,
  Play,
  Percent,
  DollarSign,
  Clock,
  AlertCircle,
  MessageSquare,
  Trash2
} from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import FlagIcon from '../../components/FlagIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBirthdayEmails = () => {
  const { token } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    discountType: 'percentage',
    discountValue: 20,
    discountValidDays: 14,
    emailDaysBefore: 0,
    applicableTo: 'all',
    selectedPasses: [],
    selectedSessions: [],
    minAccountAgeDays: 30,
    minBookingsRequired: 1,
    smsEnabled: false,
    smsTemplate: {}
  });
  const [stats, setStats] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingTestSms, setSendingTestSms] = useState(false);
  const [runningCampaign, setRunningCampaign] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [availablePasses, setAvailablePasses] = useState([]);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [fitnetTranslations, setFitnetTranslations] = useState({});
  const [deletingOldCodes, setDeletingOldCodes] = useState(false);

  useEffect(() => {
    // Wait for token to be available before fetching protected resources
    if (token) {
      fetchSettings();
      fetchStats();
      fetchFitnetTranslations();
    } else {
      // If no token, stop loading (AdminLayout will handle redirect)
      setLoading(false);
    }
    // Languages can be fetched without token (public endpoint)
    fetchLanguages();
  }, [token]);

  // Fetch translations first, then passes and sessions
  useEffect(() => {
    if (Object.keys(fitnetTranslations).length > 0) {
      fetchPasses();
      fetchSessions();
    }
  }, [fitnetTranslations]);

  const fetchFitnetTranslations = async () => {
    try {
      const response = await fetch(`${API}/admin/fitnet-translations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFitnetTranslations(data);
      }
    } catch (error) {
      console.error('Error fetching fitnet translations:', error);
      // Continue without translations
      fetchPasses();
      fetchSessions();
    }
  };

  // Helper to get translated name
  const getTranslatedName = (key, fallbackName) => {
    const translation = fitnetTranslations[key];
    if (translation) {
      // Prefer Polish name
      return translation.pl || translation.en || fallbackName;
    }
    return fallbackName;
  };

  const fetchLanguages = async () => {
    try {
      const response = await fetch(`${API}/languages`);
      if (response.ok) {
        const data = await response.json();
        setAvailableLanguages(data.filter(l => l.isActive !== false));
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
    }
  };

  const fetchPasses = async () => {
    try {
      // Use public grouped endpoint that doesn't require auth
      const response = await fetch(`${API}/passes/grouped`);
      if (response.ok) {
        const data = await response.json();
        // Flatten all passes from groups
        const allPasses = [];
        if (Array.isArray(data)) {
          data.forEach(group => {
            if (group.passes && Array.isArray(group.passes)) {
              group.passes.forEach(pass => {
                const passId = pass.variantId || pass.id;
                // Try to get translated name from fitnetTranslations
                const translatedName = getTranslatedName(passId, pass.name) || 
                                       getTranslatedName(pass.name, pass.name);
                allPasses.push({
                  id: passId,
                  name: translatedName,
                  originalName: pass.name,
                  price: pass.price,
                  groupName: getTranslatedName(group.code || group.name, group.name)
                });
              });
            }
          });
        }
        setAvailablePasses(allPasses);
      }
    } catch (error) {
      console.error('Error fetching passes:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      // Fetch all disciplines first
      const disciplinesResponse = await fetch(`${API}/disciplines`);
      if (!disciplinesResponse.ok) return;
      
      const disciplines = await disciplinesResponse.json();
      const allClassTypes = [];
      
      // Fetch class-types for each discipline (these are the actual session types from booking flow)
      for (const disc of disciplines) {
        try {
          const classTypesResponse = await fetch(`${API}/disciplines/${disc.id}/class-types`);
          if (classTypesResponse.ok) {
            const classTypes = await classTypesResponse.json();
            if (Array.isArray(classTypes)) {
              classTypes.forEach(ct => {
                // Try to get translated name from fitnetTranslations
                const translatedName = getTranslatedName(ct.code, ct.name) || 
                                       getTranslatedName(ct.name, ct.name);
                const translatedDiscipline = getTranslatedName(disc.id, disc.name) ||
                                             getTranslatedName(disc.name, disc.name);
                allClassTypes.push({
                  id: ct.code, // Use code as ID
                  code: ct.code,
                  name: translatedName,
                  originalName: ct.name,
                  disciplineId: disc.id,
                  disciplineName: translatedDiscipline
                });
              });
            }
          }
        } catch (e) {
          console.error(`Error fetching class-types for ${disc.name}:`, e);
        }
      }
      
      setAvailableSessions(allClassTypes);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/birthday-emails/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Nie udało się pobrać ustawień');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/birthday-emails/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/birthday-emails/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Ustawienia zapisane');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Nie udało się zapisać ustawień');
    } finally {
      setSaving(false);
    }
  };

  const [testLanguage, setTestLanguage] = useState('pl');
  const [lastTestCode, setLastTestCode] = useState(null);

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Wprowadź adres email');
      return;
    }

    setSendingTest(true);
    try {
      const response = await fetch(`${API}/birthday-emails/admin/test-send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ email: testEmail, language: testLanguage })
      });

      const data = await response.json();

      if (response.ok) {
        setLastTestCode(data.code);
        toast.success(
          <div>
            <p className="font-bold">Email testowy wysłany!</p>
            <p className="text-sm">Kod rabatowy: <span className="font-mono bg-black/20 px-1 rounded">{data.code}</span></p>
            <p className="text-xs text-gray-400 mt-1">Ten kod jest aktywny i można go użyć!</p>
          </div>,
          { duration: 10000 }
        );
      } else {
        throw new Error(data.error || 'Failed to send');
      }
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error('Nie udało się wysłać emaila testowego');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendTestSms = async () => {
    if (!testPhone) {
      toast.error('Wprowadź numer telefonu');
      return;
    }

    setSendingTestSms(true);
    try {
      const response = await fetch(`${API}/birthday-emails/admin/test-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ phone: testPhone, language: 'pl' })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`SMS testowy wysłany! Kod: ${data.code}`);
        setTestPhone('');
      } else {
        throw new Error(data.error || 'Failed to send');
      }
    } catch (error) {
      console.error('Error sending test SMS:', error);
      toast.error('Nie udało się wysłać SMS testowego');
    } finally {
      setSendingTestSms(false);
    }
  };

  const handleRunCampaign = async () => {
    if (!window.confirm('Czy na pewno chcesz uruchomić kampanię urodzinową? Zostaną wysłane emaile do wszystkich kwalifikujących się użytkowników.')) {
      return;
    }

    setRunningCampaign(true);
    try {
      const response = await fetch(`${API}/birthday-emails/admin/run-campaign`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Kampania zakończona! Przetworzono: ${data.processed}, Wysłano: ${data.sent}`);
        fetchStats();
      } else {
        throw new Error(data.error || 'Failed to run');
      }
    } catch (error) {
      console.error('Error running campaign:', error);
      toast.error('Nie udało się uruchomić kampanii');
    } finally {
      setRunningCampaign(false);
    }
  };

  const handleDeleteOldCodes = async () => {
    if (!window.confirm('Czy na pewno chcesz usunąć wszystkie stare kody urodzinowe? Tej operacji nie można cofnąć.')) {
      return;
    }

    setDeletingOldCodes(true);
    try {
      const response = await fetch(`${API}/birthday-emails/codes/cleanup`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Usunięto ${data.deleted} kodów urodzinowych`);
      } else {
        throw new Error(data.error || 'Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting old codes:', error);
      toast.error('Nie udało się usunąć kodów');
    } finally {
      setDeletingOldCodes(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-birthday-emails">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
            <Gift className="w-6 h-6 text-orange-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Emaile urodzinowe</h1>
            <p className="text-muted-foreground text-sm">
              Automatyczne wysyłanie kodów rabatowych z okazji urodzin
            </p>
          </div>
        </div>
        
        {/* Enable/Disable Toggle */}
        <label className="flex items-center gap-3 cursor-pointer">
          <span className={`text-sm font-medium ${settings.enabled ? 'text-green-400' : 'text-muted-foreground'}`}>
            {settings.enabled ? 'Włączone' : 'Wyłączone'}
          </span>
          <div 
            className={`w-14 h-7 rounded-full transition-colors relative ${
              settings.enabled ? 'bg-green-500' : 'bg-white/10'
            }`}
            onClick={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
          >
            <div 
              className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                settings.enabled ? 'left-8' : 'left-1'
              }`}
            />
          </div>
        </label>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-4">
        {[
          { id: 'settings', label: 'Ustawienia', icon: Settings },
          { id: 'stats', label: 'Statystyki', icon: BarChart3 },
          { id: 'test', label: 'Test', icon: Send }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id 
                ? 'bg-primary text-white' 
                : 'bg-white/5 hover:bg-white/10 text-muted-foreground'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          {/* Discount Settings */}
          <div className="glass-card p-6 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Percent className="w-5 h-5 text-primary" />
              Ustawienia rabatu
            </h3>

            <div>
              <label className="text-sm text-muted-foreground">Typ rabatu</label>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => setSettings(prev => ({ ...prev, discountType: 'percentage' }))}
                  className={`flex-1 py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                    settings.discountType === 'percentage' 
                      ? 'bg-primary text-white' 
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <Percent className="w-4 h-4" />
                  Procentowy
                </button>
                <button
                  onClick={() => setSettings(prev => ({ ...prev, discountType: 'fixed' }))}
                  className={`flex-1 py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                    settings.discountType === 'fixed' 
                      ? 'bg-primary text-white' 
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <DollarSign className="w-4 h-4" />
                  Kwotowy
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground">
                Wartość rabatu ({settings.discountType === 'percentage' ? '%' : 'PLN'})
              </label>
              <input
                type="number"
                value={settings.discountValue}
                onChange={(e) => setSettings(prev => ({ ...prev, discountValue: parseInt(e.target.value) || 0 }))}
                min="1"
                max={settings.discountType === 'percentage' ? 100 : 500}
                className="w-full mt-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 focus:border-primary focus:outline-none"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Ważność kodu (dni)</label>
              <input
                type="number"
                value={settings.discountValidDays}
                onChange={(e) => setSettings(prev => ({ ...prev, discountValidDays: parseInt(e.target.value) || 14 }))}
                min="1"
                max="90"
                className="w-full mt-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 focus:border-primary focus:outline-none"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Dotyczy</label>
              <Select
                value={settings.applicableTo}
                onValueChange={(value) => setSettings(prev => ({ 
                  ...prev, 
                  applicableTo: value,
                  selectedPasses: value !== 'selected_passes' ? [] : prev.selectedPasses,
                  selectedSessions: value !== 'selected_sessions' ? [] : prev.selectedSessions
                }))}
              >
                <SelectTrigger className="w-full mt-2 px-4 py-2 h-auto bg-white/5 rounded-lg border border-white/10 focus:border-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-white/10">
                  <SelectItem value="all" className="text-white hover:bg-white/10">Wszystkie usługi</SelectItem>
                  <SelectItem value="passes" className="text-white hover:bg-white/10">Wszystkie karnety</SelectItem>
                  <SelectItem value="sessions" className="text-white hover:bg-white/10">Wszystkie sesje</SelectItem>
                  <SelectItem value="selected_passes" className="text-white hover:bg-white/10">Wybrane karnety...</SelectItem>
                  <SelectItem value="selected_sessions" className="text-white hover:bg-white/10">Wybrane sesje...</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Selected Passes Multi-select */}
            {settings.applicableTo === 'selected_passes' && (
              <div>
                <label className="text-sm text-muted-foreground">Wybierz karnety</label>
                <div className="mt-2 max-h-48 overflow-y-auto bg-white/5 rounded-lg border border-white/10 p-2 space-y-1">
                  {availablePasses.map(pass => (
                    <label key={pass.id} className="flex items-center gap-2 p-2 hover:bg-white/5 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(settings.selectedPasses || []).includes(pass.id)}
                        onChange={(e) => {
                          const newSelected = e.target.checked
                            ? [...(settings.selectedPasses || []), pass.id]
                            : (settings.selectedPasses || []).filter(id => id !== pass.id);
                          setSettings(prev => ({
                            ...prev,
                            selectedPasses: newSelected
                          }));
                        }}
                        className="rounded border-white/20"
                      />
                      <span className="text-sm">{pass.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">({pass.groupName}) - {pass.price} zł</span>
                    </label>
                  ))}
                  {availablePasses.length === 0 && (
                    <p className="text-sm text-muted-foreground p-2">Ładowanie karnetów...</p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Wybrano: {(settings.selectedPasses || []).length} karnetów
                </p>
              </div>
            )}

            {/* Selected Sessions Multi-select */}
            {settings.applicableTo === 'selected_sessions' && (
              <div>
                <label className="text-sm text-muted-foreground">Wybierz typy sesji</label>
                <div className="mt-2 max-h-48 overflow-y-auto bg-white/5 rounded-lg border border-white/10 p-2 space-y-1">
                  {availableSessions.map(session => (
                    <label key={session.id} className="flex items-center gap-2 p-2 hover:bg-white/5 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(settings.selectedSessions || []).includes(session.id)}
                        onChange={(e) => {
                          const newSelected = e.target.checked
                            ? [...(settings.selectedSessions || []), session.id]
                            : (settings.selectedSessions || []).filter(id => id !== session.id);
                          setSettings(prev => ({
                            ...prev,
                            selectedSessions: newSelected
                          }));
                        }}
                        className="rounded border-white/20"
                      />
                      <span className="text-sm">{session.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">({session.disciplineName})</span>
                    </label>
                  ))}
                  {availableSessions.length === 0 && (
                    <p className="text-sm text-muted-foreground p-2">Ładowanie sesji...</p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Wybrano: {(settings.selectedSessions || []).length} typów sesji
                </p>
              </div>
            )}

            {/* Info about applicableItems variable */}
            {(settings.applicableTo === 'selected_passes' || settings.applicableTo === 'selected_sessions') && (
              <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <p className="text-sm text-purple-300">
                  <strong>Zmienna:</strong> <code className="bg-black/30 px-1 rounded">{`{{applicableItems}}`}</code>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Nazwy wybranych usług zostaną automatycznie pobrane z tłumaczeń FitNet 
                  w języku preferowanym przez użytkownika.
                </p>
              </div>
            )}
          </div>

          {/* Campaign Settings */}
          <div className="glass-card p-6 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Ustawienia kampanii
            </h3>

            <div>
              <label className="text-sm text-muted-foreground">Wyślij email (dni przed urodzinami)</label>
              <input
                type="number"
                value={settings.emailDaysBefore}
                onChange={(e) => setSettings(prev => ({ ...prev, emailDaysBefore: parseInt(e.target.value) || 0 }))}
                min="0"
                max="7"
                className="w-full mt-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 focus:border-primary focus:outline-none"
              />
              <p className="text-xs text-muted-foreground mt-1">
                0 = w dniu urodzin, 1 = dzień przed, itd.
              </p>
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Minimalny wiek konta (dni)</label>
              <input
                type="number"
                value={settings.minAccountAgeDays}
                onChange={(e) => setSettings(prev => ({ ...prev, minAccountAgeDays: parseInt(e.target.value) || 0 }))}
                min="0"
                max="365"
                className="w-full mt-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 focus:border-primary focus:outline-none"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Ochrona przed nadużyciami - świeżo założone konta nie dostaną kodu
              </p>
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Minimalna liczba rezerwacji</label>
              <input
                type="number"
                value={settings.minBookingsRequired}
                onChange={(e) => setSettings(prev => ({ ...prev, minBookingsRequired: parseInt(e.target.value) || 0 }))}
                min="0"
                max="10"
                className="w-full mt-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 focus:border-primary focus:outline-none"
              />
              <p className="text-xs text-muted-foreground mt-1">
                0 = bez wymagań, 1+ = wymaga historii rezerwacji
              </p>
            </div>
          </div>

          {/* SMS Settings */}
          <div className="glass-card p-6 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Powiadomienia SMS
            </h3>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Włącz SMS</p>
                <p className="text-xs text-muted-foreground">
                  Wyślij SMS po wysłaniu emaila urodzinowego
                </p>
              </div>
              <div 
                className={`w-14 h-7 rounded-full transition-colors relative cursor-pointer ${
                  settings.smsEnabled ? 'bg-green-500' : 'bg-white/10'
                }`}
                onClick={() => setSettings(prev => ({ ...prev, smsEnabled: !prev.smsEnabled }))}
              >
                <div 
                  className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                    settings.smsEnabled ? 'left-8' : 'left-1'
                  }`}
                />
              </div>
            </div>

            {settings.smsEnabled && (
              <>
                {/* Dynamic SMS templates for all active languages */}
                {availableLanguages.map(lang => (
                  <div key={lang.code}>
                    <label className="text-sm text-muted-foreground flex items-center gap-2">
                      <span><FlagIcon code={lang.code} size={16} /></span>
                      Szablon SMS ({lang.name})
                    </label>
                    <textarea
                      value={settings.smsTemplate?.[lang.code] || ''}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        smsTemplate: { ...prev.smsTemplate, [lang.code]: e.target.value }
                      }))}
                      rows={3}
                      className="w-full mt-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 focus:border-primary focus:outline-none resize-none text-sm"
                      placeholder={lang.code === 'pl' 
                        ? "Wszystkiego najlepszego! Twój kod {{discountCode}}..." 
                        : "Happy Birthday! Your code {{discountCode}}..."}
                    />
                  </div>
                ))}
                
                <p className="text-xs text-muted-foreground">
                  Zmienne: {'{{discountCode}}'}, {'{{discountValue}}'}, {'{{validUntil}}'}, {'{{firstName}}'}
                </p>

                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-xs text-blue-400">
                    SMS zostanie wysłany tylko do użytkowników z numerem telefonu, po wysłaniu emaila.
                    Polskie znaki zostaną automatycznie zamienione na ASCII.
                  </p>
                </div>
              </>
            )}

            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="w-full btn-primary flex items-center justify-center gap-2 mt-4"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Check className="w-5 h-5" />
              )}
              Zapisz ustawienia
            </button>
          </div>
        </motion.div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && stats && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="glass-card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalSent}</p>
                  <p className="text-xs text-muted-foreground">Wysłanych emaili</p>
                </div>
              </div>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalUsed}</p>
                  <p className="text-xs text-muted-foreground">Wykorzystanych kodów</p>
                </div>
              </div>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.conversionRate}%</p>
                  <p className="text-xs text-muted-foreground">Konwersja (ogółem)</p>
                </div>
              </div>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.last30Days?.sent || 0}</p>
                  <p className="text-xs text-muted-foreground">Ostatnie 30 dni</p>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Codes Table */}
          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Ostatnio wysłane kody
            </h3>

            {stats.recentCodes && stats.recentCodes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-muted-foreground border-b border-white/10">
                      <th className="pb-3">Użytkownik</th>
                      <th className="pb-3">Kod</th>
                      <th className="pb-3">Urodziny</th>
                      <th className="pb-3">Wysłano</th>
                      <th className="pb-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentCodes.map((code, index) => (
                      <tr key={index} className="border-b border-white/5">
                        <td className="py-3">
                          <p className="font-medium">{code.userName}</p>
                          <p className="text-xs text-muted-foreground">{code.userEmail}</p>
                        </td>
                        <td className="py-3 font-mono text-sm">{code.code}</td>
                        <td className="py-3 text-sm">{code.birthDate?.split('T')[0]}</td>
                        <td className="py-3 text-sm">{new Date(code.sentAt).toLocaleDateString('pl-PL')}</td>
                        <td className="py-3">
                          {code.used ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                              <Check className="w-3 h-3" />
                              Wykorzystany
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                              <Clock className="w-3 h-3" />
                              Oczekuje
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                Brak wysłanych kodów urodzinowych
              </p>
            )}
          </div>
        </motion.div>
      )}

      {/* Test Tab */}
      {activeTab === 'test' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          {/* Send Test Email */}
          <div className="glass-card p-6 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Wyślij testowy email
            </h3>
            <p className="text-sm text-muted-foreground">
              Wyślij testowy email urodzinowy z <strong>działającym kodem rabatowym</strong>.
              Zobaczysz dokładnie to co otrzyma klient.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Adres email</label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="w-full mt-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 focus:border-primary focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Język emaila</label>
                <Select
                  value={testLanguage}
                  onValueChange={(value) => setTestLanguage(value)}
                >
                  <SelectTrigger className="w-full mt-2 px-4 py-2 h-auto bg-gray-800 text-white rounded-lg border border-white/10 focus:border-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-white/10">
                    {availableLanguages.length > 0 ? (
                      availableLanguages.map(lang => (
                        <SelectItem key={lang.code} value={lang.code} className="text-white hover:bg-white/10">
                          <FlagIcon code={lang.code} size={16} /> {lang.nativeName || lang.name || lang.code.toUpperCase()}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="pl" className="text-white hover:bg-white/10">🇵🇱 Polski</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {lastTestCode && (
              <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                <p className="text-sm text-green-300">
                  Ostatni wygenerowany kod: <code className="bg-black/30 px-2 py-0.5 rounded font-mono">{lastTestCode}</code>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Ten kod jest aktywny i można go użyć w systemie.
                </p>
              </div>
            )}

            <button
              onClick={handleSendTest}
              disabled={sendingTest || !testEmail}
              className="w-full btn-secondary flex items-center justify-center gap-2"
            >
              {sendingTest ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
              Wyślij test
            </button>
          </div>

          {/* Send Test SMS */}
          <div className="glass-card p-6 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Wyślij testowy SMS
            </h3>
            <p className="text-sm text-muted-foreground">
              Wyślij testowy SMS urodzinowy z przykładowym kodem rabatowym.
            </p>

            <div>
              <label className="text-sm text-muted-foreground">Numer telefonu</label>
              <input
                type="tel"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+48 500 500 500"
                className="w-full mt-2 px-4 py-2 bg-white/5 rounded-lg border border-white/10 focus:border-primary focus:outline-none"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Możesz podać numer z lub bez prefiksu kraju
              </p>
            </div>

            <button
              onClick={handleSendTestSms}
              disabled={sendingTestSms || !testPhone}
              className="w-full btn-secondary flex items-center justify-center gap-2"
            >
              {sendingTestSms ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <MessageSquare className="w-5 h-5" />
              )}
              Wyślij test SMS
            </button>
          </div>

          {/* Manual Campaign Run */}
          <div className="glass-card p-6 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Play className="w-5 h-5 text-primary" />
              Ręczne uruchomienie
            </h3>
            <p className="text-sm text-muted-foreground">
              Uruchom kampanię ręcznie, aby wysłać emaile do wszystkich użytkowników z urodzinami dzisiaj.
            </p>

            <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-orange-400">Uwaga!</p>
                  <p className="text-muted-foreground">
                    Kampania automatycznie uruchamia się codziennie o 9:00. 
                    Użyj tej opcji tylko do testów lub w wyjątkowych sytuacjach.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleRunCampaign}
              disabled={runningCampaign || !settings.enabled}
              className="w-full btn-primary flex items-center justify-center gap-2"
            >
              {runningCampaign ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Play className="w-5 h-5" />
              )}
              Uruchom kampanię
            </button>
            
            {!settings.enabled && (
              <p className="text-xs text-center text-red-400">
                Włącz kampanię w ustawieniach, aby móc ją uruchomić
              </p>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminBirthdayEmails;
